import React, { useState } from "react";
import styles from "./book-explore.module.css";

import bookToc from "./book-toc.json";

const topics = {
  "fertility": "Fertility & Childbirth",
  "gynecology": "Gynecology & Sexual Health",
  "menopause": "Perimenopause & Menopause",
  "exercise": "Exercise & Nutrition",
  "midlife": "Midlife Health",
  "mental": "Mental Health",
  "systems": "Body Systems",
}

interface ChapterData {
  id: number;
  number: string;
  title: string;
  summary: string;
  experts: string[];
  topics?: string[];
}

const CHAPTERS: ChapterData[] = bookToc;

// Extract unique topics from the chapters
// Extract unique topics from the chapters
const ALL_TOPICS = Array.from(new Set(CHAPTERS.flatMap(c => c.topics || [])))
  .filter(topic => Object.keys(topics).includes(topic)); // Only show topics defined in our mapping

// Map display names
const formatTopic = (topic: string) => {
  return topics[topic as keyof typeof topics] || topic.charAt(0).toUpperCase() + topic.slice(1);
};


export default function BookExplore() {
  const [activeChapterId, setActiveChapterId] = useState(1);
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);

  const activeChapter = CHAPTERS.find(c => c.id === activeChapterId) || CHAPTERS[0];

  const handleTopicClick = (topic: string) => {
    if (selectedTopic === topic) {
      setSelectedTopic(null);
    } else {
      setSelectedTopic(topic);

      // Find first chapter with this topic and set it as active if the current one isn't relevant
      const firstRelevantChapter = CHAPTERS.find(c => c.topics?.includes(topic));
      if (firstRelevantChapter && (!activeChapter.topics?.includes(topic))) {
        setActiveChapterId(firstRelevantChapter.id);
      }
    }
  };

  const isChapterRelevant = (chapter: ChapterData) => {
    if (!selectedTopic) return true;
    return chapter.topics?.includes(selectedTopic);
  };

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>Inside the Book</h2>
          <p className={styles.subtitle}>A Chapter-by-Chapter Breakdown</p>
        </div>

        {/* Filter Toolbar */}
        <div className={styles.topicToolbar}>
          <span className={styles.filterLabel}>Filter by Topic:</span>
          <div className={styles.topicList}>
            {ALL_TOPICS.map(topic => (
              <button
                key={topic}
                className={`${styles.topicButton} ${selectedTopic === topic ? styles.activeTopic : ''}`}
                onClick={() => handleTopicClick(topic)}
              >
                {formatTopic(topic)}
              </button>
            ))}
          </div>
          {selectedTopic && (
            <button className={styles.clearFilter} onClick={() => setSelectedTopic(null)}>
              Clear Filter
            </button>
          )}
        </div>

        <div className={styles.explorerWrapper}>
          {/* Sidebar List */}
          <div className={styles.sidebar}>
            {CHAPTERS.map((chapter) => (
              <div
                key={chapter.id}
                className={`
                  ${styles.chapterItem} 
                  ${chapter.id === activeChapterId ? styles.active : ''}
                  ${!isChapterRelevant(chapter) ? styles.dimmed : ''}
                `}
                onClick={() => {
                  if (isChapterRelevant(chapter)) {
                    setActiveChapterId(chapter.id);
                  }
                }}
              >
                <span className={styles.itemNumber}>{chapter.number}</span>
                <span className={styles.itemTitle}>{chapter.title}</span>
              </div>
            ))}
          </div>

          {/* Main Content Detail */}
          <div className={styles.mainContent}>
            <div key={activeChapter.id} className={styles.contentContainer}>
              <span className={styles.chapterLabel}>{activeChapter.number}</span>
              <h3 className={styles.contentTitle}>{activeChapter.title}</h3>
              <p className={styles.contentSummary}>{activeChapter.summary}</p>

              <div className={styles.metaSection}>
                <h4 className={styles.metaLabel}>Experts Consulted</h4>
                <div className={styles.expertList}>
                  {activeChapter.experts.map((expert, idx) => (
                    <span key={idx} className={styles.expertTag}>{expert}</span>
                  ))}
                  {/* Fallback for empty lists if any */}
                  {activeChapter.experts.length === 0 && (
                    <span className={styles.expertTag}>Panel of Specialists</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
